import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

interface LoadingOverlayProps {
  isVisible: boolean;
  country?: string;
  city?: string;
}

const loadingMessages = [
  { text: (country: string) => `Searching for ${country}...`, duration: 3000 },
  { text: (country: string) => `Finding amazing places to visit...`, duration: 4600 },
  { text: (country: string) => `Discovering local attractions...`, duration: 5300 },
  { text: (country: string, city?: string) => `Creating your ${city || country} itinerary...`, duration: 3000 },
  { text: (country: string) => `Adding transportation options...`, duration: 5200 },
  { text: (country: string) => `Finalizing your perfect guide...`, duration: 300000 },
];

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ isVisible, country = 'destination', city }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setCurrentMessageIndex(0);
      setCurrentText('');
      setIsAnimating(false);
      return;
    }

    const message = loadingMessages[currentMessageIndex];
    if (!message) return;

    setCurrentText(message.text(country, city));
    setIsAnimating(true);

    const timer = setTimeout(() => {
      setIsAnimating(false);

      // Move to next message after animation completes
      setTimeout(() => {
        setCurrentMessageIndex((prev) =>
          prev < loadingMessages.length - 1 ? prev + 1 : prev
        );
      }, 500); // Half second delay between messages
    }, message.duration);

    return () => clearTimeout(timer);
  }, [isVisible, currentMessageIndex, country, city]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 3,
          padding: 4,
          maxWidth: 400,
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Loading spinner */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            border: '4px solid',
            borderColor: 'primary.light',
            borderTopColor: 'primary.main',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />

        {/* Animated text */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            minHeight: '2em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: isAnimating ? 'translateY(0)' : 'translateY(20px)',
            opacity: isAnimating ? 1 : 0,
            transition: 'all 0.5s ease-in-out',
            animation: isAnimating ? 'slideUp 0.5s ease-out' : 'none',
            '@keyframes slideUp': {
              '0%': {
                transform: 'translateY(20px)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              },
            },
          }}
        >
          {currentText}
        </Typography>

        {/* Progress indicator */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 3,
          }}
        >
          {loadingMessages.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: index <= currentMessageIndex ? 'primary.main' : 'grey.300',
                transition: 'background-color 0.3s ease',
                animation: index === currentMessageIndex ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingOverlay;
